import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { type Portfolio } from "../../lib/api";
import { navigate } from "../../lib/route";
import { StatCard } from "./Cards";

const HOST = window.location.hostname === "localhost" ? "professionals.test.wiebe.xyz" : window.location.hostname;


// ─── active dashboard ─────────────────────────────────────────────────────────

export function ActiveDashboard({
  portfolio,
  stats,
  onNewInquiry,
}: {
  portfolio: Portfolio;
  stats: {
    bookings_total: number;
    bookings_upcoming: number;
    bookings_inquiry: number;
    galleries_total: number;
    galleries_public: number;
    clients_total: number;
    leads_total: number;
    packages_public: number;
  };
  onNewInquiry: () => void;
}) {
  const { t } = useTranslation();
  const portfolioURL = `https://${HOST}/${portfolio.Slug}${!portfolio.IsPublished ? "?preview=true" : ""}`;
  const firstName = portfolio.DisplayName.split(" ")[0];
  const h = new Date().getHours();
  const period = h < 5 ? "night" : h < 12 ? "morning" : h < 17 ? "afternoon" : h < 22 ? "evening" : "night";
  const variant = useMemo(() => Math.floor(Math.random() * 4), []);
  const greeting = t(`dashboard.greeting_${period}_${variant}`, { name: firstName });

  const checklistSteps = [
    { label: t("dashboard.setup_publish"), done: portfolio.IsPublished, href: "portfolio" },
    { label: t("dashboard.setup_bio"), done: portfolio.Bio.trim().length > 0, href: "portfolio" },
    { label: t("dashboard.setup_gallery"), done: stats.galleries_total > 0, href: "galleries" },
    { label: t("dashboard.setup_client"), done: stats.clients_total > 0, href: "clients" },
  ];
  const allChecklistDone = checklistSteps.every((s) => s.done);

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "0.75rem",
          marginBottom: "1.5rem",
        }}
      >
        <h1 style={{ margin: 0 }}>{greeting}</h1>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <button className="btn-ghost" onClick={() => navigate("galleries")} style={{ fontSize: "0.875rem" }}>{t("dashboard.new_gallery")}</button>
          <button className="btn-ghost" onClick={() => navigate("bookings")} style={{ fontSize: "0.875rem" }}>{t("dashboard.new_booking")}</button>
          <button className="btn-ghost" onClick={() => navigate("clients")} style={{ fontSize: "0.875rem" }}>{t("dashboard.add_client")}</button>
          <button className="btn-primary" onClick={onNewInquiry} style={{ fontSize: "0.875rem", whiteSpace: "nowrap" }}>
            {t("dashboard.new_inquiry")}
          </button>
          <button
            className="btn-ghost"
            onClick={() => window.open(portfolioURL, "_blank", "noopener,noreferrer")}
            style={{ fontSize: "0.875rem", whiteSpace: "nowrap" }}
          >
            {t("dashboard.view_site")}
          </button>
        </div>
      </div>

      {!allChecklistDone && (
        <div className="card" style={{ marginBottom: "1.5rem", padding: "1rem 1.25rem" }}>
          <h3 style={{ margin: "0 0 0.75rem", fontSize: "0.875rem", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--muted)", fontWeight: 600 }}>
            {t("dashboard.setup_title")}
          </h3>
          <div style={{ display: "flex", gap: "1.25rem", flexWrap: "wrap" }}>
            {checklistSteps.map((step) =>
              step.done ? (
                <span key={step.label} style={{ display: "flex", alignItems: "center", gap: "0.35rem", fontSize: "0.875rem", color: "var(--muted)" }}>
                  <span style={{ color: "var(--accent)", fontWeight: 700 }}>✓</span>
                  {step.label}
                </span>
              ) : (
                <a
                  key={step.label}
                  href={`#/${step.href}`}
                  style={{ display: "flex", alignItems: "center", gap: "0.35rem", fontSize: "0.875rem", color: "var(--text)", textDecoration: "none" }}
                  onClick={(e) => { e.preventDefault(); navigate(step.href); }}
                >
                  <span style={{ color: "var(--muted)" }}>○</span>
                  {step.label}
                </a>
              )
            )}
          </div>
        </div>
      )}

      <div
        style={{
          display: "grid",
          gap: "1rem",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          alignItems: "stretch",
          marginBottom: "1.5rem",
        }}
      >
        <StatCard
          title={t("dashboard.upcoming_bookings")}
          value={stats.bookings_upcoming}
          sub={
            stats.bookings_inquiry > 0
              ? `${stats.bookings_inquiry} pending inquiry`
              : undefined
          }
          href="/app/bookings"
        />
        <StatCard
          title={t("dashboard.galleries")}
          value={stats.galleries_total}
          sub={`${stats.galleries_public} public`}
          href="/app/galleries"
        />
        <StatCard
          title={t("dashboard.clients")}
          value={stats.clients_total}
          href="/app/clients"
        />
        <StatCard
          title={t("dashboard.leads")}
          value={stats.leads_total}
          sub={t("dashboard.stat_from_form")}
          href="/app/leads"
        />
        <StatCard
          title={t("dashboard.packages")}
          value={stats.packages_public}
          sub={t("dashboard.stat_public_offerings")}
          href="/app/packages"
        />
      </div>

      {stats.bookings_inquiry > 0 && (
        <div
          className="alert"
          style={{
            background: "color-mix(in srgb, var(--accent) 12%, transparent)",
            borderColor: "var(--accent)",
            color: "var(--text)",
            marginBottom: stats.leads_total > 0 ? "0.5rem" : undefined,
          }}
        >
          You have {stats.bookings_inquiry} unconfirmed{" "}
          {stats.bookings_inquiry === 1 ? "inquiry" : "inquiries"}.{" "}
          <a href="/app/bookings?status=inquiry" style={{ color: "var(--accent)" }}>
            Review now →
          </a>
        </div>
      )}
      {stats.leads_total > 0 && (
        <div
          className="alert"
          style={{
            background: "color-mix(in srgb, var(--muted) 10%, transparent)",
            borderColor: "var(--border)",
            color: "var(--text)",
          }}
        >
          {stats.leads_total} new {stats.leads_total === 1 ? "lead" : "leads"} from your contact form.{" "}
          <a href="/app/leads" style={{ color: "var(--accent)" }}>
            View leads →
          </a>
        </div>
      )}
    </div>
  );
}

